import { prettyKind } from './libraryKinds';
import OpenPdfButton from './OpenPdfButton';

/**
 * One document in the library - the same row in the list view and inside a folder.
 *
 * Extracted from `DocumentLibrary.tsx` when the folder view arrived (FV, 2026-09-08) so that a
 * folder's rows and the list's rows are ONE component: the File it / Open / remove behaviours
 * DOC1b-DOC1d built must not exist twice.
 */

export interface Filing {
  linkId: string;
  targetType: string;
  targetId: string;
  label: string;
  resolved: boolean;
  category: string | null;
  visibility: string;
}

export interface LibraryFile {
  id: string;
  filename: string;
  sizeBytes: number | null;
  createdAt: string | null;
  filings: Filing[];
  needsAHome: boolean;
  /** DOC1b: the address this document's NAME suggests. A search term, never a selection. */
  filingHint: string | null;
  /** DOC1d: two spellings of one address share a key. See `agreedHint`. */
  filingHintKey: string | null;
  /** DOC1c: the KIND of document, or null when nothing has said yet. */
  documentType: string | null;
  /** E1.4: how it arrived, when it arrived by email. Null for everything that did not. */
  mail: MailProvenance | null;
  /**
   * E1.5a: has anybody actually READ it. Four values; see `Readability`.
   * ⛔⛔ OPTIONAL ON PURPOSE, AND IT IS A DEPLOY-ORDER FACT, NOT LAZINESS. Vercel and Railway deploy from
   *    the same push INDEPENDENTLY, so there is a window where this new page is talking to the old API,
   *    whose rows carry no `readability` at all. Reading `.verdict` off `undefined` would white-screen the
   *    Documents page for that window. Absent is treated as `unchecked`, which is also literally true of
   *    an API that never checked anything - so it is honest as well as safe, and self-heals on deploy.
   */
  readability?: Readability;
  /**
   * LOI-PDF1b (the 130th): this document can ALSO be opened as a real PDF made from its Word file - a letter, on a
   * server that has the converter. ⛔ OPTIONAL for the same deploy-order reason as `readability`: absent is the old
   * API, which offered no PDF - so the row keeps its one "Open".
   */
  pdf?: boolean;
}

/**
 * E1.5a - whether anybody has read this document.
 *
 * ⛔⛔⛔ `unchecked` EXISTS BECAUSE ITS ABSENCE WAS THE BUG. E1.4 badged only what the MAILBOX judged, and
 *    226 of 230 files had never been looked at - rendering exactly like a document that had been read.
 *    "Nobody looked" and "readable" must never draw the same.
 */
export interface Readability {
  verdict: 'unchecked' | 'not-attempted' | 'readable' | 'unreadable' | 'empty';
  chars: number | null;
  reason: string | null;
}

/**
 * E1.4 - what the mailbox knows about a document in the library.
 *
 * ⭐⭐ "ARRIVED", NOT "CAME FROM". A document already in the library that is then emailed to him gets a
 *   mail record too - the same bytes arriving again is exactly what the sha256 dedupe is for - so this
 *   says the mailbox SAW it, never that the mailbox is where it originated.
 */
export interface MailProvenance {
  arrivals: number;
  from: string | null;
  subject: string | null;
  sentAtRaw: string | null;
  labelName: string | null;
  disposition: string;
  /** ⛔⛔ Any arrival was held: nobody has read this document. See the badge below. */
  everHeld: boolean;
  heldReason: string | null;
}

/** Where a filing sends you. A `deal_room` and an `escrow` are surfaces of one deal, so all three
 *  deal-ish types land on the deal. An unresolved filing links nowhere - there is nothing to open. */
export const hrefFor = (f: Filing): string | null => {
  if (!f.resolved) return null;
  switch (f.targetType) {
    case 'property': return `/properties/${f.targetId}`;
    case 'master_property': return `/data-hub/${f.targetId}`;
    case 'deal': case 'deal_room': case 'escrow': return `/crm/deals/${f.targetId}`;
    case 'contact': return `/crm/contacts/${f.targetId}`;
    case 'company': return `/crm/companies/${f.targetId}`;
    default: return null;
  }
};

const prettySize = (bytes: number | null): string | null => {
  if (!bytes || bytes <= 0) return null;
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const prettyDate = (iso: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/** ⭐ `mime_type` is NULL on 81 of 99 rows - measured. Dispatch on the EXTENSION, never the column;
 *  a badge keyed on mime would read "unknown" for 82% of the folder. */
const extensionOf = (filename: string): string => {
  const dot = filename.lastIndexOf('.');
  return dot > 0 ? filename.slice(dot + 1).toUpperCase() : 'FILE';
};

interface Props {
  file: LibraryFile;
  selected: boolean;
  onToggle: () => void;
  onFileIt: () => void;
  onOpen: () => void;
  opening: boolean;
  /**
   * LOI-PDF1b: the letter's PDF - how to ask for its link, and what the page does when it cannot be made. Required,
   * so a new caller of this row cannot forget a letter has one. The button itself is `OpenPdfButton`.
   */
  signPdf: () => Promise<string>;
  onPdfStart: () => void;
  onPdfFailed: (message: string) => void;
  /**
   * Why this row's document did not open, or null. ⛔ Said ON THE ROW (found by looking, the 130th): the page's own
   * message strip sits under the whole list, so on a long list a window flashed and the reason was off the bottom of
   * the screen - a refusal he cannot see is a dead button.
   */
  openError: string | null;
  onUnfile: (filing: Filing) => void;
  removing: string | null;
}

export default function LibraryFileRow({
  file: f, selected, onToggle, onFileIt, onOpen, opening, signPdf, onPdfStart, onPdfFailed, openError, onUnfile,
  removing,
}: Props) {
  /** ⛔ Absent = the old API, which checked nothing. `unchecked` is the honest and the safe reading. */
  const verdict = f.readability?.verdict ?? 'unchecked';

  return (
    <li data-file-row className="py-3">
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        {/* ⭐ Wrapped, not bare: a 14px checkbox is an unhittable target on a phone, and an
            `input` is a replaced element that cannot be enlarged without drawing a giant box.
            The LABEL carries the 44px tap area (see the coarse-pointer rule in index.css); the
            box keeps its size. */}
        <label
          className="-my-1 flex shrink-0 cursor-pointer items-center self-center pr-1"
          aria-label={`Select ${f.filename}`}
        >
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggle}
            className="h-3.5 w-3.5 accent-gray-900"
          />
        </label>
        <span className="min-w-0 flex-1 basis-[240px] break-words text-[13px] font-medium text-gray-800">
          {f.filename}
        </span>
        <span className="text-[11.5px] text-gray-400">
          {/* ⭐ The KIND leads: it is what the kind filter asks about, so the row has to be able
              to answer for itself why it matched. */}
          {[f.documentType ? prettyKind(f.documentType) : null,
            extensionOf(f.filename), prettySize(f.sizeBytes), prettyDate(f.createdAt)]
            .filter(Boolean).join(' · ')}
        </span>
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            data-file-it
            onClick={onFileIt}
            className="rounded border border-gray-300 px-2.5 py-1 text-[11.5px] font-semibold text-gray-700 hover:bg-gray-50"
          >
            File it
          </button>
          {/* LOI-PDF1b: a letter has TWO things to open, and each button names its own (a control names its
              contents): the PDF - the letter as it prints, header and footer - and the Word file, which is what
              "Open" always opened. Every other row keeps its one "Open". */}
          {f.pdf && <OpenPdfButton sign={signPdf} onStart={onPdfStart} onFailed={onPdfFailed} />}
          <button
            type="button"
            onClick={onOpen}
            disabled={opening}
            className="rounded border border-gray-300 px-2.5 py-1 text-[11.5px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {opening ? 'Opening...' : f.pdf ? 'Word file' : 'Open'}
          </button>
        </div>
      </div>

      {openError && (
        <p role="alert" className="mt-1.5 rounded border border-red-300 bg-red-50 px-2.5 py-1.5 text-[12px] leading-snug text-red-700">
          {openError}
        </p>
      )}

      <div className="mt-1.5 flex flex-wrap items-baseline gap-1.5">
        {/* ⭐⭐⭐ THE 15. A blank here would read as still-loading; this row is the only place in
            the app such a document appears at all. */}
        {/* ⛔⛔⛔ E1.4 - NOBODY HAS READ THIS DOCUMENT, AND IT SITS BESIDE A "File it" BUTTON.
            An image-only scan looks exactly like an ordinary PDF in this list. Filing one onto a deal
            is the 4253 Cesar Chavez defect a level up - there, ten characters over ten pages read as
            "clean" and a document carrying two brokers' details reached a client portal. It is RED, it
            is first, and it NAMES what is wrong rather than saying "held". */}
        {/* ⛔ An EMPTY file is not a scan. "Not readable yet" would send him to read nothing; the action
            here is to replace or delete it. Measured: one of 230, a failed upload from 2026-08-15. */}
        {verdict === 'empty' && (
          <span
            data-testid="library-empty-badge"
            className="rounded border border-red-300 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-800"
          >
            Empty file
          </span>
        )}
        {verdict === 'unreadable' && (
          <span
            data-testid="library-held-badge"
            className="rounded border border-red-300 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-800"
          >
            Not readable yet
          </span>
        )}
        {/* ⛔⛔ THE STATE THAT USED TO BE INVISIBLE, AND IT IS DELIBERATELY QUIET. It states a FACT
            ("nobody looked"), it does not claim a problem - so it is grey, not red. A red chip on every
            unbackfilled row would be the cry-wolf failure, and a weakened badge proves nothing. */}
        {verdict === 'unchecked' && (
          <span
            data-testid="library-unchecked-badge"
            className="rounded border border-gray-300 bg-gray-50 px-2 py-0.5 text-[11px] text-gray-500"
            title="Nobody has checked whether this document can be read"
          >
            Not checked
          </span>
        )}
        {f.needsAHome ? (
          <span className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800">
            Needs a home - not filed anywhere yet
          </span>
        ) : (
          f.filings.map((x) => {
            const href = hrefFor(x);
            const body = (
              <>
                {x.label}
                {x.category && x.category !== 'other' && (
                  <span className="text-gray-400"> · {x.category}</span>
                )}
              </>
            );
            return (
              <span
                key={x.linkId}
                className="inline-flex items-center gap-1 rounded border border-gray-300 bg-gray-50 px-2 py-0.5 text-[11px]"
              >
                {href ? (
                  <a href={href} className="text-gray-700 hover:underline">{body}</a>
                ) : (
                  <span className="text-gray-500">{body}</span>
                )}
                {/* The correction path. Removing a filing never touches the file. */}
                <button
                  type="button"
                  aria-label={`Remove this document from ${x.label}`}
                  onClick={() => onUnfile(x)}
                  disabled={removing === x.linkId}
                  className="text-gray-400 hover:text-red-600 disabled:opacity-40"
                >
                  {removing === x.linkId ? '...' : '×'}
                </button>
              </span>
            );
          })
        )}
      </div>

      {/* ⛔⛔ THE REASON GETS ITS OWN LINE, NOT THE BADGE. Measured before drawing it: the four real
          held reasons run 60 to 170 characters ("image-only pages: 10 character(s) of text across the
          whole document (under 20). It must be rendered and read page by page..."). A sentence that long
          inside a pill wraps into an unreadable red block - the badge NAMES the problem, this SAYS it. */}
      {(verdict === 'unreadable' || verdict === 'empty') && f.readability?.reason && (
        <div data-testid="library-held-reason" className="mt-1 text-[11px] leading-snug text-red-700">
          {f.readability.reason}
        </div>
      )}

      {/* ⭐ E1.4 - the envelope, so a document he is about to file is not an anonymous filename.
          Sender first: on the real corpus that is the one field that tells him whose document it is. */}
      {f.mail && (
        <div data-testid="library-mail-provenance" className="mt-1 text-[11px] text-gray-500">
          <span className="font-medium text-gray-600">Arrived by email</span>
          {f.mail.from && <> · {f.mail.from}</>}
          {f.mail.subject && <> · <span className="italic">{f.mail.subject}</span></>}
          {f.mail.sentAtRaw && <> · {f.mail.sentAtRaw}</>}
          {/* ⭐ More than one arrival is normal, not a defect - a counter forwarded twice is one
              document. Said plainly so nobody reads it as a duplicate to clean up. */}
          {f.mail.arrivals > 1 && <> · arrived {f.mail.arrivals} times</>}
        </div>
      )}
    </li>
  );
}
